import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.bigimage';
import 'leaflet.bigimage/dist/Leaflet.BigImage.min.css';
import EventsApi from '../../api/eventsApi/eventsApi';
import { blobToBase64 } from '../home/components/reportComponent/reportUtils';

const customMarkerIcon = new L.Icon({
    iconUrl: '/placeholder.png',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -48],
});

export const OSM_TILE = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
/** Esri World Imagery — aynı projeksiyon, yalnızca raster katman değişir */
export const SATELLITE_TILE =
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

function FacilityCircles({ lat, lng, radius }: { lat: number; lng: number; radius: number }) {
    const ring = {
        color: '#ff0000' as const,
        fillColor: 'transparent' as const,
        weight: 2,
        opacity: 1,
        fillOpacity: 0,
    };
    return (
        <>
            <Circle center={[lat, lng]} radius={radius} pathOptions={ring} />
            <Circle center={[lat, lng]} radius={radius / 2} pathOptions={ring} />
            <Circle center={[lat, lng]} radius={radius / 4} pathOptions={ring} />
        </>
    );
}

async function safePrepareMapForCapture(map: L.Map, lat: number, lng: number, radius: number): Promise<void> {
    await new Promise<void>((r) => {
        map.whenReady(r);
    });

    for (let i = 0; i < 80; i++) {
        map.invalidateSize({ animate: false });
        const sz = map.getSize();
        if (sz.x > 0 && sz.y > 0) break;
        await new Promise((r) => setTimeout(r, 40));
    }

    await new Promise((r) => requestAnimationFrame(r));

    try {
        const bounds = L.circle([lat, lng], { radius }).getBounds();
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    } catch (e) {
        console.warn('[ReportLeafletCapture] fitBounds atlandı:', e);
    }

    await waitForTilesQuiet(map);
}

function captureMapToPngBlob(map: L.Map): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const t = window.setTimeout(() => reject(new Error('BigImage timeout')), 12000);
        let settled = false;
        const finishOk = (blob: Blob) => {
            if (settled) return;
            settled = true;
            clearTimeout(t);
            resolve(blob);
        };
        const finishErr = (e: Error) => {
            if (settled) return;
            settled = true;
            clearTimeout(t);
            reject(e);
        };

        try {
            const BigImageCtor = (window as unknown as { L: { Control: { BigImage: new (opts: object) => any } } }).L
                .Control.BigImage;
            const bigImageControl = new BigImageCtor({
                position: 'topright',
                title: 'Save Map',
                printControlLabel: '💾',
                maxScale: 1,
                minScale: 1,
                inputTitle: 'Kopya Sayısı',
                downloadTitle: 'Rapora Ekle',
            });

            const originalPrint = bigImageControl._print;
            let gotBlob = false;

            bigImageControl._print = function (this: {
                canvas?: HTMLCanvasElement & { toBlob?: typeof HTMLCanvasElement.prototype.toBlob };
            }) {
                originalPrint.call(this);
                const self = this;
                if (self.canvas && self.canvas.toBlob) {
                    const canvas = self.canvas;
                    const originalToBlob = canvas.toBlob.bind(canvas);
                    canvas.toBlob = function (
                        _cb: (blob: Blob | null) => void,
                        type?: string,
                        quality?: number,
                    ) {
                        originalToBlob((blob: Blob | null) => {
                            if (gotBlob) return;
                            if (blob) {
                                gotBlob = true;
                                finishOk(blob);
                            } else {
                                finishErr(new Error('Empty PNG blob'));
                            }
                        }, type ?? 'image/png', quality);
                    };
                } else {
                    finishErr(new Error('BigImage canvas not available'));
                }
            };

            map.addControl(bigImageControl);
            window.setTimeout(() => {
                const controlButton = document.querySelector('.leaflet-bigimage-control') as HTMLElement;
                if (controlButton) controlButton.style.display = 'none';
                const popup = document.querySelector('.leaflet-bigimage-popup') as HTMLElement;
                if (popup) popup.style.display = 'none';
            }, 0);

            bigImageControl._print();

            window.setTimeout(() => {
                try {
                    map.removeControl(bigImageControl);
                } catch {
                    // ignore
                }
            }, 600);
        } catch (e) {
            finishErr(e instanceof Error ? e : new Error(String(e)));
        }
    });
}

async function waitForTilesQuiet(map: L.Map): Promise<void> {
    await Promise.race([
        new Promise<void>((resolve) => {
            const layers: L.TileLayer[] = [];
            map.eachLayer((layer) => {
                if (layer instanceof L.TileLayer) layers.push(layer);
            });
            if (layers.length === 0) {
                resolve();
                return;
            }
            let remaining = layers.length;
            const done = () => {
                remaining--;
                if (remaining <= 0) resolve();
            };
            layers.forEach((tl) => {
                tl.once('load', done);
            });
            window.setTimeout(resolve, 5000);
        }),
        new Promise<void>((r) => setTimeout(r, 5000)),
    ]);
    await new Promise((r) => setTimeout(r, 350));
}

export type ReportLeafletCaptureMode = 'both' | 'satelliteOnly' | 'standardOnly';

export interface ReportLeafletCaptureProps {
    pinId: string;
    pinName: string;
    latitude: number;
    longitude: number;
    radiusMeters: number;
    /**
     * both: önce OSM, sonra uydu.
     * satelliteOnly: kayıtlı standart var; yalnızca uydu.
     * standardOnly: kayıtlı uydu var; yalnızca standart OSM (ikinci kare yok).
     */
    captureMode: ReportLeafletCaptureMode;
    /** Standart PNG üretildiyse ve bu oturumda kayıt uygunsa sunucuya yükle */
    uploadStandardAsReportLocation: boolean;
    /** Uydu PNG için `ReportLocationSatellitePng` (dosya adında `harita-uydu`) */
    uploadSatelliteAsReportLocation: boolean;
    onStandardCaptured: (dataUrl: string) => void;
    onSatelliteCaptured: (dataUrl: string) => void;
    onStandardFailed: () => void;
    onSatelliteFailed: () => void;
}

const CAPTURE_W = 1180;
const CAPTURE_H = 640;

/** Harita bağlamını üst bileşene iletir (whenReady + boyut için pipeline bekler). */
const MapContextBridge: React.FC<{ onMap: (m: L.Map) => void }> = ({ onMap }) => {
    const map = useMap();
    useEffect(() => {
        onMap(map);
    }, [map, onMap]);
    return null;
};

const DualLayerCapturePipeline: React.FC<{
    map: L.Map | null;
    lat: number;
    lng: number;
    radius: number;
    captureMode: ReportLeafletCaptureMode;
    tileUrl: string;
    setTileUrl: (u: string) => void;
    uploadStandardAsReportLocation: boolean;
    uploadSatelliteAsReportLocation: boolean;
    pinId: string;
    pinName: string;
    onStandardCaptured: (dataUrl: string) => void;
    onSatelliteCaptured: (dataUrl: string) => void;
    onStandardFailed: () => void;
    onSatelliteFailed: () => void;
}> = ({
    map,
    lat,
    lng,
    radius,
    captureMode,
    tileUrl,
    setTileUrl,
    uploadStandardAsReportLocation,
    uploadSatelliteAsReportLocation,
    pinId,
    pinName,
    onStandardCaptured,
    onSatelliteCaptured,
    onStandardFailed,
    onSatelliteFailed,
}) => {
    const settledRef = useRef(false);
    const phaseDoneRef = useRef({ osm: false, sat: false });

    useEffect(() => {
        if (!map) return;
        let cancelled = false;

        const run = async () => {
            const isOsm = tileUrl === OSM_TILE;

            if (
                isOsm &&
                (captureMode === 'both' || captureMode === 'standardOnly') &&
                phaseDoneRef.current.osm
            ) {
                return;
            }
            if (!isOsm && phaseDoneRef.current.sat) return;

            try {
                await safePrepareMapForCapture(map, lat, lng, radius);
                if (cancelled || settledRef.current) return;

                const blob = await captureMapToPngBlob(map);
                if (cancelled || settledRef.current) return;

                if (isOsm && (captureMode === 'both' || captureMode === 'standardOnly')) {
                    if (phaseDoneRef.current.osm) return;
                    phaseDoneRef.current.osm = true;

                    if (uploadStandardAsReportLocation) {
                        void (async () => {
                            try {
                                const formData = new FormData();
                                const ts = Date.now();
                                const safePinName = pinName.replace(/[\\/:*?"<>|]+/g, '_');
                                formData.append('Files', blob, `${safePinName}_harita-${ts}.png`);
                                formData.append('Id', pinId);
                                formData.append('UserId', localStorage.getItem('userId') || '');
                                await EventsApi.updatePin(pinId, formData);
                                EventsApi.refreshPinFullReport(pinId);
                            } catch (err) {
                                console.warn('[ReportLeafletCapture] Standart harita yüklenemedi:', err);
                            }
                        })();
                    }
                    const dataUrl = await blobToBase64(blob);
                    if (cancelled) return;
                    onStandardCaptured(dataUrl);
                    if (captureMode === 'both') {
                        setTileUrl(SATELLITE_TILE);
                    }
                    return;
                }

                if (!isOsm) {
                    if (phaseDoneRef.current.sat) return;
                    phaseDoneRef.current.sat = true;

                    if (uploadSatelliteAsReportLocation) {
                        void (async () => {
                            try {
                                const formData = new FormData();
                                const ts = Date.now();
                                const safePinName = pinName.replace(/[\\/:*?"<>|]+/g, '_');
                                formData.append('Files', blob, `${safePinName}_harita-uydu-${ts}.png`);
                                formData.append('Id', pinId);
                                formData.append('UserId', localStorage.getItem('userId') || '');
                                await EventsApi.updatePin(pinId, formData);
                                await EventsApi.refreshPinFullReport(pinId);
                            } catch (err) {
                                console.warn('[ReportLeafletCapture] Uydu haritası yüklenemedi:', err);
                            }
                        })();
                    }

                    const dataUrl = await blobToBase64(blob);
                    if (cancelled) return;
                    settledRef.current = true;
                    onSatelliteCaptured(dataUrl);
                }
            } catch (e) {
                console.warn('[ReportLeafletCapture] Yakalama:', isOsm ? 'standart' : 'uydu', e);
                if (cancelled || settledRef.current) return;

                if (isOsm && captureMode === 'both') {
                    if (!phaseDoneRef.current.osm) {
                        phaseDoneRef.current.osm = true;
                        onStandardFailed();
                    }
                    setTileUrl(SATELLITE_TILE);
                    return;
                }

                if (isOsm && captureMode === 'standardOnly') {
                    if (!phaseDoneRef.current.osm) {
                        phaseDoneRef.current.osm = true;
                        onStandardFailed();
                    }
                    return;
                }

                settledRef.current = true;
                if (!phaseDoneRef.current.sat) {
                    phaseDoneRef.current.sat = true;
                    onSatelliteFailed();
                }
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [
        map,
        tileUrl,
        lat,
        lng,
        radius,
        captureMode,
        uploadStandardAsReportLocation,
        uploadSatelliteAsReportLocation,
        pinId,
        pinName,
        onStandardCaptured,
        onSatelliteCaptured,
        onStandardFailed,
        onSatelliteFailed,
        setTileUrl,
    ]);

    return null;
};

const ReportLeafletCapture: React.FC<ReportLeafletCaptureProps> = ({
    pinId,
    pinName,
    latitude,
    longitude,
    radiusMeters,
    captureMode,
    uploadStandardAsReportLocation,
    uploadSatelliteAsReportLocation,
    onStandardCaptured,
    onSatelliteCaptured,
    onStandardFailed,
    onSatelliteFailed,
}) => {
    const initialTile =
        captureMode === 'satelliteOnly' ? SATELLITE_TILE : OSM_TILE;
    const [tileUrl, setTileUrl] = useState(initialTile);
    const [mapInstance, setMapInstance] = useState<L.Map | null>(null);

    return (
        <div
            style={{
                position: 'fixed',
                left: -16000,
                top: 0,
                width: CAPTURE_W,
                height: CAPTURE_H,
                minWidth: CAPTURE_W,
                minHeight: CAPTURE_H,
                zIndex: -1,
                overflow: 'hidden',
                pointerEvents: 'none',
                visibility: 'hidden' as const,
            }}
            aria-hidden
        >
            <MapContainer
                center={[latitude, longitude]}
                zoom={14}
                style={{ width: CAPTURE_W, height: CAPTURE_H }}
                scrollWheelZoom={false}
                dragging={false}
                doubleClickZoom={false}
                boxZoom={false}
                keyboard={false}
                attributionControl={false}
                zoomControl={false}
            >
                <MapContextBridge onMap={setMapInstance} />
                <TileLayer key={tileUrl} url={tileUrl} attribution="" maxZoom={19} />
                <FacilityCircles lat={latitude} lng={longitude} radius={radiusMeters} />
                <Marker position={[latitude, longitude]} icon={customMarkerIcon} />
                {mapInstance ? (
                    <DualLayerCapturePipeline
                        map={mapInstance}
                        lat={latitude}
                        lng={longitude}
                        radius={radiusMeters}
                        captureMode={captureMode}
                        tileUrl={tileUrl}
                        setTileUrl={setTileUrl}
                        uploadStandardAsReportLocation={uploadStandardAsReportLocation}
                        uploadSatelliteAsReportLocation={uploadSatelliteAsReportLocation}
                        pinId={pinId}
                        pinName={pinName}
                        onStandardCaptured={onStandardCaptured}
                        onSatelliteCaptured={onSatelliteCaptured}
                        onStandardFailed={onStandardFailed}
                        onSatelliteFailed={onSatelliteFailed}
                    />
                ) : null}
            </MapContainer>
        </div>
    );
};

export default ReportLeafletCapture;
